import { useMemo, useState } from 'react'
import { priorityBoard, BANDS } from '../../../lib/cheerPriority.js'
import { incumbent } from '../../../lib/cheerIncumbentData.js'
import NextStep from './NextStep.jsx'
import styles from './cheer.module.css'
import shared from './pursuit.module.css'

/**
 * THE WORK ORDER — who to call first, and the line to open with.
 *
 * Brian: "build the best tool for my team."
 *
 * The Cheer space knows a great deal: 519 gyms, a competitor's activity tier on
 * each, the price their families pay, a championship footprint. Sorted by name,
 * that is a spreadsheet. This is the same data as a DAY'S WORK.
 *
 * ⚠️ THE REASON IS THE PRODUCT, NOT THE RANK. Every row shows the evidence that
 * put it there and what it means, because a ranking a rep cannot interrogate is
 * one they either follow blindly or ignore — and both are worse than no ranking.
 * The score exists only to order the list; it is deliberately not the headline.
 *
 * ⚠️ AND IT SHOWS ITS OWN BLIND SPOT. The count of gyms we can say NOTHING
 * about is on screen beside the actionable ones. A worklist that quietly drops
 * everything it has no opinion on lets an enrichment gap read as a short
 * pipeline, and a manager then pushes the team instead of fixing the data.
 */
export default function CheerWorkOrder({ items, onOpen, rowAction = null }) {
  const [band, setBand] = useState('all')
  const [limit, setLimit] = useState(50)

  const board = useMemo(() => priorityBoard(items || []), [items])

  const rows = useMemo(() => (
    band === 'all' ? board.rows : board.rows.filter((r) => r.priority.band === band)
  ), [board, band])

  const shown = rows.slice(0, limit)

  return (
    <div className={styles.wrap}>
      <div className={styles.definition}>
        <strong>The work order.</strong> Every gym ranked by the evidence we actually hold, with
        the reason on the row. A competitor who has gone quiet outranks one still selling; a gym
        whose families already pay premium outranks one buying on price. Existing partners sort
        to the bottom — this is a prospecting list, not an account list.
      </div>

      {/* ⚠️ THE DENOMINATOR AND THE BLIND SPOT, side by side. */}
      <div className={styles.scoreRow}>
        {BANDS.map((b) => (
          <Score
            key={b.key}
            v={(board.counts[b.key] || 0).toLocaleString()}
            k={b.label}
            sub={b.hint}
            gap={b.key === 'none' && (board.counts.none || 0) > 0}
          />
        ))}
      </div>

      <div className={styles.controls}>
        <label className={styles.control}>
          <span>Show</span>
          <select className="input" value={band} onChange={(e) => { setBand(e.target.value); setLimit(50) }}>
            <option value="all">Everything ({board.total.toLocaleString()})</option>
            {BANDS.map((b) => (
              <option key={b.key} value={b.key}>
                {b.label} ({(board.counts[b.key] || 0).toLocaleString()})
              </option>
            ))}
          </select>
        </label>
        <span className={styles.controlNote}>
          {rows.length.toLocaleString()} {rows.length === 1 ? 'gym' : 'gyms'}
          {board.noSignal > 0 && band === 'all' && (
            <> · {board.noSignal.toLocaleString()} of them have nothing recorded to open on —
              that is an enrichment gap, not a short pipeline</>
          )}
        </span>
      </div>

      <div className={styles.teams}>
        {shown.map(({ gym, priority }) => (
          <WorkRow
            key={gym.id || gym.name}
            gym={gym}
            priority={priority}
            onOpen={onOpen}
            rowAction={rowAction}
          />
        ))}
        {!shown.length && <p className={styles.colEmpty}>No gyms in this band.</p>}
      </div>

      {rows.length > shown.length && (
        <button type="button" className={styles.more} onClick={() => setLimit(limit + 100)}>
          Show 100 more
          <span className={styles.moreOf}> · {shown.length.toLocaleString()} of {rows.length.toLocaleString()}</span>
        </button>
      )}
    </div>
  )
}

function Score({ v, k, sub, gap }) {
  return (
    <div className={`${styles.score} ${gap ? styles.scoreGap : ''}`}>
      <div className={styles.scoreV}>{v}</div>
      <div className={styles.scoreK}>{k}</div>
      {sub && <div className={styles.scoreSub}>{sub}</div>}
    </div>
  )
}

function WorkRow({ gym, priority, onOpen, rowAction }) {
  const [open, setOpen] = useState(false)
  const inc = incumbent(gym)

  return (
    <article className={styles.card} style={{ '--tone': priority.tone }}>
      <div className={styles.cardTop}>
        <button type="button" className={styles.cardName} onClick={() => onOpen?.(gym)} title="Open the full gym record">
          {gym.name}<span className={styles.openHint}> ›</span>
        </button>
        <span className={shared.badgeDim} style={{ color: priority.tone }}>{priority.label}</span>
        {gym.state && gym.state !== 'NATIONAL' && <span className={shared.badgeDim}>{gym.state}</span>}
      </div>

      {/* THE LINE A REP READS. Always the strongest POSITIVE signal, so it is
          something to act on rather than a reason to hesitate. */}
      <p className={styles.colHint}>{priority.headline}</p>

      {/* ⚠️ THE EVIDENCE, one click away and never hidden behind a tooltip.
          A reason a rep cannot check is a reason they will not repeat to a
          customer — and repeating it to the customer is the entire point. */}
      {priority.signals.length > 0 && (
        <button type="button" className={styles.footprint} onClick={() => setOpen(!open)}>
          {open ? 'hide why' : `why — ${priority.signals.length} signal${priority.signals.length === 1 ? '' : 's'}`}
        </button>
      )}
      {open && (
        <ul className={styles.teams}>
          {priority.signals.map((s) => (
            <li key={s.key} className={styles.sportRow}>
              <span className={styles.sportName}>{s.what}</span>
              <span className={styles.sportConf}>{s.why}</span>
              {s.evidence && (
                <a
                  className={shared.badgeDim}
                  href={s.evidence}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  title="The competitor storefront this was read from — the receipt"
                >
                  see the evidence ↗
                </a>
              )}
            </li>
          ))}
          {/* ⚠️ TWO DIFFERENT DATES, AND THIS LINE HAD THEM CONFUSED.
              `lastPublished` is when the COMPETITOR last added a ring.
              `incumbentChecked` is when WE last looked. Labelling the first as
              "last checked" told a rep our intel was three years stale when it
              was captured today — which would stop them trusting a row that is
              in fact current, and is the opposite of the reassurance the line
              is for. Show the as-of date, and show it only when we have one. */}
          {(gym.incumbentChecked || gym.sr_incumbent_checked) && (
            <li className={styles.sportRow}>
              <span className={styles.sportConf}>
                Competitor storefront last checked by SR on {gym.incumbentChecked || gym.sr_incumbent_checked}
                {inc.lastPublished ? ` — they last added a ring ${inc.lastPublished}.` : '.'}
                {' '}A status with no as-of date is not one to quote at a customer.
              </span>
            </li>
          )}
        </ul>
      )}

      {rowAction && <NextStep record={gym} action={rowAction} onOpen={onOpen} />}
    </article>
  )
}
